import _objectSpread from "@babel/runtime/helpers/objectSpread2";

/* eslint-disable no-console */

/**
 * Get a logger object with `error()`, `warn`, `info`, `debug` and `log()` functions, which
 * - print to browser console when called in the browser in a dev environment.
 * - do nothing when called in the browser in a non-dev environment (unless the the local-storage has "nw.logging.level.global" set)
 * - print using console.log to the server console when called on the server in development mode.
 * - output JSON to the server console when called on the server in non-development mode.
 *
 * This logger is smart enough to distinguish between server-side/client-side and dev/prod execution.
 *
 * - All output contains a timestamp in the iso format.
 * - All output contains the logger name to simplify filtering using dev-tools or kibana.
 * - When NODE_ENV=debug, we will print simple console.foo messages on the console on both server side and client side.
 * - When NODE_ENV!=debug
 *     - on client-side
 *         - We will never log anything unless overridden using a local-storage flag.
 *     - on server-side
 *         - We will not print logger.debug messages on the server-side unless you change the global log level by calling
 *          `setLoggingLevel()`; that's intended to experiment with turning logging off/on based on (say) cookies.
 *         - All output will be in a JSON format that our logstash can pick up.
 *         - All output will have the right log level field set so that you can filter on that.
 *
 * @example
 * ```
 * import { getLogger } from '@nerdwallet/js-logging';
 *
 * const logger = getLogger('<my-module>'); // this will cause all your log messages to be tagged with <my-module>
 * // Note that logger.(log|debug|error|warn|info) all have the signature as console.log.
 * logger.debug('foo');                                  // [iso time][<my-module>] foo
 * logger.debug('I was called with', { arg1, arg2 });    // [iso time][<my-module>] I was called with >{ arg1 … }
 * logger.error('This here error', err);                 // [iso time][<my-module>] This here error > { message… }
 *
 * // You can also create child loggers for each function in a module (say a logger for each action in redux)
 * const rootLogger = getLogger('actions');
 * const someLongAction = (arg1, arg2, arg3) =>
 *   async (dispatch) => {
 *     const logger = getLogger('someLongAction', rootLogger);
 *     logger.debug('called with', arg1, arg2, arg3);    // [iso time][actions > someLongAction] called with arg1 arg2 arg3
 *     try {
 *       await someThing();
 *     } catch (err) {
 *       logger.error(err);                              // [iso time][actions > someLongAction] >{ object containing error message, stack trace etc.}
 *     }
 *   }
 * ```
 */
import { logInner } from './lib/logInner';
import { Level } from './constants';
export { setSentryClient } from './sentry';
export { setLoggingLevel, getLoggingLevel } from './lib/loggingLevel';
export { Level };

var getCallerClientID = function getCallerClientID(req) {
  var _req$headers$xCaller, _req$headers, _req$query;

  return (// look for client_id, first in headers, fallback to query params
    (_req$headers$xCaller = req === null || req === void 0 ? void 0 : (_req$headers = req.headers) === null || _req$headers === void 0 ? void 0 : _req$headers['x-caller-client-id']) !== null && _req$headers$xCaller !== void 0 ? _req$headers$xCaller : req === null || req === void 0 ? void 0 : (_req$query = req.query) === null || _req$query === void 0 ? void 0 : _req$query.client_id
  );
};
/**
 * A named Logger object which prefixes each console.log (or error) call with the logger's
 * name, making it easier to trace where a given console message originated from.
 */


/**
 * @param loggerName - name of the logger. This will be prefixed to the output.
 * @param options - Either a parent logger instance, or an options object
 * @param {object} options.serverRequest  - the request object. If provided, will be used to supply tags to the log.
 * @param {string} options.parentLogger - a parent logger instance. If supplied, the new logger will have a logger name which incorporates the parent's name.
 *
 * @example
 * ```
 * import { getLogger } from '@nerdwallet/logging';
 * const logger = getLogger('my-awesome-action-creator');
 * ...
 * ...
 * try {
 *  throw('something broke');
 * } catch (err) {
 *  logger.error(err);
 * }
 * ```
 *
 * This will output to the console the following
 * ```
 * iso time string [my-awesome-action-creator] Error: something broke
 *     at vendor.bundle.blah:3784637643764
 *     at ugirugdj...
 * ```
 */
export var getLogger = function getLogger(loggerName) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var argOptions; // options is the parentLogger

  if (options !== null && options !== void 0 && options.getName) {
    argOptions = {
      parentLogger: options
    };
  } else {
    argOptions = _objectSpread({}, options);
  }

  if (argOptions.parentLogger) {
    // A child logger!
    return getLogger("".concat(argOptions.parentLogger.getName(), " > ").concat(loggerName), _objectSpread(_objectSpread(_objectSpread({}, argOptions.parentLogger.getOptions()), argOptions), {}, {
      parentLogger: undefined
    }));
  }
  /* Local, instance specific logging level. We initialize this to undefined because
  `shouldLog` relies on this being undefined in order to decide whether or not a local
  logging level was ever set. */


  var localLevel;
  var tags = {};

  if (options !== null && options !== void 0 && options.serverRequest) {
    var _ref = options,
        serverRequest = _ref.serverRequest;
    tags.caller_client_id = getCallerClientID(serverRequest);
    tags.method = serverRequest.method;
    tags.endpoint = serverRequest.originalUrl;
    tags.request_id = serverRequest.requestId;
  }

  return {
    log: function log() {
      for (var _len = arguments.length, messages = new Array(_len), _key = 0; _key < _len; _key++) {
        messages[_key] = arguments[_key];
      }

      return logInner({
        level: Level.info,
        localLevel: localLevel,
        messages: messages,
        loggerName: loggerName,
        tags: tags
      });
    },
    error: function error() {
      for (var _len2 = arguments.length, messages = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        messages[_key2] = arguments[_key2];
      }

      return logInner({
        level: Level.error,
        localLevel: localLevel,
        messages: messages,
        loggerName: loggerName,
        tags: tags
      });
    },
    warn: function warn() {
      for (var _len3 = arguments.length, messages = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        messages[_key3] = arguments[_key3];
      }

      return logInner({
        level: Level.warn,
        localLevel: localLevel,
        messages: messages,
        loggerName: loggerName,
        tags: tags
      });
    },
    debug: function debug() {
      for (var _len4 = arguments.length, messages = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
        messages[_key4] = arguments[_key4];
      }

      return logInner({
        level: Level.debug,
        localLevel: localLevel,
        messages: messages,
        loggerName: loggerName,
        tags: tags
      });
    },
    info: function info() {
      for (var _len5 = arguments.length, messages = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
        messages[_key5] = arguments[_key5];
      }

      return logInner({
        level: Level.info,
        localLevel: localLevel,
        messages: messages,
        loggerName: loggerName,
        tags: tags
      });
    },
    getName: function getName() {
      return loggerName;
    },
    getOptions: function getOptions() {
      return argOptions;
    },
    setLoggingLevel: function setLoggingLevel(newLevel) {
      localLevel = newLevel;
    },
    clearLoggingLevel: function clearLoggingLevel() {
      localLevel = undefined;
    }
  };
};